import { useMemo, type CSSProperties } from 'react';
import { addDays, buildOccurrences, startOfDay } from '@budget/core';
import {
  useCalendarEvents,
  type CalendarEvent,
} from '../../../data/useCalendarEvents';
import { useKioskBills } from '../../../data/useKioskBills';
import { useNow } from '../../../data/kioskRefresh';
import { useScrollFade } from '../useScrollFade';

// Kitchen kiosk "Upcoming" widget. Three tracks merged into one card:
//  - Calendar events (family + holidays) from /api/calendar — the start time
//    is shown next to the date ("WED @ 9:00PM")
//  - Bills due + paydays (income) from the dashboard sheet, expanded into
//    concrete due-dates via @budget/core's buildOccurrences
//  - Expirations (passports/IDs/registrations) — PINNED to the bottom so a
//    renewal never scrolls out of view; the rest of the list scrolls behind it.
//
// The scrolling list has soft top/bottom edge fades (only on the side that has
// more content), mirroring the AnyList card.

const HORIZON_DAYS = 7; // window for events + bills + paydays
const MAX_NORMAL_EVENTS = 8;
const EXPIRATION_LEAD_DAYS = 90; // expirations look further ahead (pinned at bottom)
const EXPIRATION_AMBER_DAYS = 30;
const EXPIRATION_RED_DAYS = 7;

interface UpcomingEventsWidgetProps {
  className?: string;
}

// One rendered line. Built up-front (incl. its day label) so the renderer is
// dumb and doesn't need `now`.
interface Row {
  id: string;
  date: Date;
  kindLabel: string; // "Bill" / "Payday" / "Holiday" / "Event" / "Expires"
  kindClass: string;
  dotClass: string; // timeline marker fill/outline
  dotColor?: string; // --dot-color for the dot-glow halo (colored markers only)

  label: string; // the day (e.g. "Today" / "Wed"), pushed to the right
  labelClass: string;
  time?: string; // start time for timed events, shown under the date
  title: string;
  titleClass: string;
}

function eventLocalDate(event: CalendarEvent): Date {
  if (event.allDay) {
    const [y, m, d] = event.start.slice(0, 10).split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1);
  }
  return new Date(event.start);
}

function eventLocalEnd(event: CalendarEvent): Date {
  if (event.allDay) {
    // Google's all-day end is exclusive — Aug 1 single-day event ends at
    // 2026-08-02. Treat it as ongoing until midnight on the last covered day.
    const [y, m, d] = event.end.slice(0, 10).split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1);
  }
  return new Date(event.end);
}

function dayDiff(a: Date, b: Date): number {
  const ad = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const bd = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((ad.getTime() - bd.getTime()) / (24 * 60 * 60 * 1000));
}

function dayLabel(d: Date, now: Date): string {
  const diff = dayDiff(d, now);
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff < 7) return d.toLocaleDateString('en-US', { weekday: 'short' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Date-label tint by proximity: today and tomorrow render as filled pills
// (green / amber), everything further out is plain tertiary text.
const PILL_BASE = 'inline-block rounded-full px-2 py-1 font-semibold leading-none';
function dayLabelClass(d: Date, now: Date): string {
  const diff = dayDiff(d, now);
  if (diff <= 0) return `${PILL_BASE} bg-green-soft text-green`;
  if (diff === 1) return `${PILL_BASE} bg-amber-soft text-amber-light`;
  return 'text-fg-tertiary font-semibold';
}

// Timeline marker. Colored markers carry `dotColor` so the standardized
// `dot-glow` utility (driven by --dot-color) adds the soft halo; the empty grey
// circle has no glow. Its `bg-elevated` fill keeps the rail line from showing
// through the middle.
interface Marker {
  dotClass: string;
  dotColor?: string;
}
const EMPTY_MARKER: Marker = {
  dotClass: 'bg-subtle outline outline-1 outline-border-strong',
};
const REMINDER_MARKER: Marker = {
  dotClass: 'bg-rose-light dot-glow-ring',
  dotColor: 'var(--color-rose-light)',
};
// Proximity marker for non-reminder rows: today = green, tomorrow = amber-light,
// else the empty grey circle.
function markerFor(d: Date, now: Date): Marker {
  const diff = dayDiff(d, now);
  if (diff <= 0) return { dotClass: 'bg-green dot-glow-ring', dotColor: 'var(--color-green)' };
  if (diff === 1)
    return { dotClass: 'bg-amber-light dot-glow-ring', dotColor: 'var(--color-amber-light)' };
  return EMPTY_MARKER;
}

// Kind-label color matched to the row's timeline dot: today = green,
// tomorrow = amber, further out = the neutral tone of the empty dot.
function kindClassFor(d: Date, now: Date): string {
  const diff = dayDiff(d, now);
  if (diff <= 0) return 'text-green';
  if (diff === 1) return 'text-amber-light';
  return 'text-fg-secondary';
}

// Full clock time for the date label, e.g. "9:00PM".
function timeFull(event: CalendarEvent): string {
  const d = new Date(event.start);
  const h12 = ((d.getHours() + 11) % 12) + 1;
  const m = d.getMinutes();
  const ampm = d.getHours() < 12 ? 'AM' : 'PM';
  return `${h12}:${m.toString().padStart(2, '0')}${ampm}`;
}

function expirationTitleClass(event: CalendarEvent, now: Date): string {
  const diff = dayDiff(eventLocalDate(event), now);
  if (diff <= EXPIRATION_RED_DAYS) return 'text-red';
  if (diff <= EXPIRATION_AMBER_DAYS) return 'text-amber';
  return 'text-fg';
}

function renderRow(row: Row, i: number, rows: Row[], trailingFade = false) {
  // The rail line only connects between dots — never a stub above the first dot
  // or below the last. First row draws the bottom half, last row the top half,
  // middle rows the full span, and a lone row draws no line at all.
  const isFirst = i === 0;
  const isLast = i === rows.length - 1;
  const lineSpan = isFirst && isLast
    ? null
    : isFirst
      ? 'top-1/2 bottom-0'
      : isLast
        ? 'top-0 bottom-1/2'
        : 'inset-y-0';
  // The scrolling list's last dot drops a short line below itself that fades to
  // transparent (hints there's nothing more), instead of stopping at the dot.
  const showTrailingFade = trailingFade && isLast;
  return (
    <li key={row.id} className="flex items-stretch gap-3 text-sm">
      {/* Timeline rail: connecting line + a marker centered on the row. */}
      <div className="relative w-3 shrink-0 flex items-center justify-center">
        {lineSpan && (
          <span
            className={`absolute left-1/2 -translate-x-1/2 w-px bg-border ${lineSpan}`}
            aria-hidden
          />
        )}
        {showTrailingFade && (
          // Fixed length (~4–5 rows) rather than bottom-0 so the tail drops well
          // past the last row and fades over the whole run. Absolute, so it only
          // shows in empty space below the list and doesn't add scroll height.
          <span
            className="absolute left-1/2 top-1/2 -translate-x-1/2 w-px h-24"
            style={{ background: 'linear-gradient(to bottom, var(--color-border), transparent)' }}
            aria-hidden
          />
        )}
        <span
          className={`relative w-2.5 h-2.5 rounded-full ${row.dotClass}`}
          style={row.dotColor ? ({ '--dot-color': row.dotColor } as CSSProperties) : undefined}
          aria-hidden
        />
      </div>
      <div className="flex-1 min-w-0 flex items-center justify-between gap-3 py-2">
        <div className="flex flex-col min-w-0 flex-1">
          <span className={`text-[0.625rem] uppercase tracking-widest ${row.kindClass}`}>
            {row.kindLabel}
          </span>
          <span className={`truncate font-semibold ${row.titleClass}`}>{row.title}</span>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-1 text-[0.625rem] uppercase tracking-widest tabular leading-tight">
          <span className={row.labelClass}>{row.label}</span>
          {row.time && <span className="text-[0.575rem] text-fg-tertiary">{row.time}</span>}
        </div>
      </div>
    </li>
  );
}

export function UpcomingEventsWidget({ className }: UpcomingEventsWidgetProps = {}) {
  const { events, stale, error } = useCalendarEvents();
  const { bills } = useKioskBills();
  const now = useNow(60 * 1000);

  const { scrollRows, expirationRows } = useMemo(() => {
    const today = startOfDay(now);
    const cutoff = now.getTime();
    const horizonEnd = addDays(today, HORIZON_DAYS);

    // --- Calendar events ---
    const future = events.filter((ev) => eventLocalEnd(ev).getTime() > cutoff);

    // Reminders within the 90-day lead window. Those inside the 7-day horizon
    // get interleaved into the scroll list; the rest are pinned at the bottom.
    const reminders = future.filter(
      (ev) =>
        ev.source === 'expirations' &&
        dayDiff(eventLocalDate(ev), now) <= EXPIRATION_LEAD_DAYS,
    );
    // `pinned` reminders (beyond the horizon, pinned at the bottom) keep the
    // amber/red proximity tint on the title; in-scroll reminders use the plain
    // foreground tone so the list doesn't read as all-urgent.
    const reminderRow = (ev: CalendarEvent, pinned = false): Row => {
      const date = eventLocalDate(ev);
      return {
        id: ev.id,
        date,
        kindLabel: 'Reminder',
        kindClass: 'text-rose-light',
        ...REMINDER_MARKER,
        label: dayLabel(date, now),
        // Today/Tomorrow reminders get the pill shape like any other row, but in
        // the rose reminder color; anything further out is plain rose text.
        labelClass:
          dayDiff(date, now) <= 1
            ? `${PILL_BASE} bg-rose-soft text-rose-light`
            : 'text-rose-light font-semibold',
        title: ev.title,
        titleClass: pinned ? expirationTitleClass(ev, now) : 'text-fg',
      };
    };

    const normalEvents = future
      .filter((ev) => ev.source !== 'expirations' && eventLocalDate(ev) <= horizonEnd)
      .slice(0, MAX_NORMAL_EVENTS);

    // --- Bills + paydays (income) from the sheet ---
    const occ = buildOccurrences(bills ?? [], today, horizonEnd);

    // --- Scroll rows: events + bills + paydays + in-window reminders ---
    const scroll: Row[] = [];
    for (const ev of normalEvents) {
      scroll.push({
        id: ev.id,
        date: eventLocalDate(ev),
        kindLabel: ev.source === 'holidays' ? 'Holiday' : 'Event',
        kindClass: kindClassFor(eventLocalDate(ev), now),
        ...markerFor(eventLocalDate(ev), now),
        label: dayLabel(eventLocalDate(ev), now),
        labelClass: dayLabelClass(eventLocalDate(ev), now),
        time: ev.allDay ? undefined : timeFull(ev),
        title: ev.title,
        titleClass: 'text-fg',
      });
    }
    for (const o of occ) {
      scroll.push({
        id: `${o.bill.type}|${o.bill.name}|${o.date.toISOString().slice(0, 10)}`,
        date: o.date,
        kindLabel: o.bill.type === 'income' ? 'Payday' : 'Bill',
        kindClass: kindClassFor(o.date, now),
        ...markerFor(o.date, now),
        label: dayLabel(o.date, now),
        labelClass: dayLabelClass(o.date, now),
        title: o.bill.name,
        titleClass: 'text-fg',
      });
    }
    for (const ev of reminders) {
      if (dayDiff(eventLocalDate(ev), now) <= HORIZON_DAYS) scroll.push(reminderRow(ev));
    }
    scroll.sort((a, b) => a.date.getTime() - b.date.getTime());

    // --- Pinned: reminders beyond the 7-day window (within 90) ---
    const pinned = reminders
      .filter((ev) => dayDiff(eventLocalDate(ev), now) > HORIZON_DAYS)
      .sort((a, b) => a.start.localeCompare(b.start))
      .map((ev) => reminderRow(ev, true));

    return { scrollRows: scroll, expirationRows: pinned };
  }, [events, bills, now]);

  // Soft, time-based top/bottom edge fades on the scroll area (shared hook).
  const scrollFade = useScrollFade([scrollRows]);

  const nothing = scrollRows.length === 0 && expirationRows.length === 0;

  return (
    <div className={`card flex flex-col gap-3 ${className ?? ''}`}>
      <header className="flex items-center justify-between">
        <h2 className="text-[0.625rem] uppercase tracking-widest text-fg-secondary">
          Upcoming
        </h2>
        {stale && (
          <span className="text-[0.625rem] uppercase tracking-widest text-amber">
            stale
          </span>
        )}
      </header>

      {error && nothing ? (
        <div className="text-sm text-fg-tertiary py-2">Calendar unavailable</div>
      ) : (
        <>
          <div
            ref={scrollFade.ref}
            onScroll={scrollFade.onScroll}
            // -ml-3/pl-3 extends the clip box left into the card padding so the
            // marker dot-glow halos aren't cut off (overflow-y-auto clips x too).
            // -mr-3/pr-3 mirrors it on the right so the scrollbar sits in the
            // card's padding gutter instead of overlapping the date labels.
            className="flex-1 min-h-0 overflow-y-auto -ml-3 pl-3 -mr-3 pr-3"
            style={scrollFade.style}
          >
            {scrollRows.length === 0 ? (
              <div className="text-sm text-fg-tertiary py-2">Nothing coming up</div>
            ) : (
              <ul className="flex flex-col">
                {scrollRows.map((row, i, rows) => renderRow(row, i, rows, true))}
              </ul>
            )}
          </div>
          {expirationRows.length > 0 && (
            <div className="shrink-0 border-t border-border pt-2">
              <ul className="flex flex-col">{expirationRows.map(renderRow)}</ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
